<template>
  <PortalWidget
    :widget="widget"
    class="table-widget"
  >
    <template #headerContent>
      <div class="header-container flex--justify-end">
        <slot name="headerContent" />
        <v-select
          v-if="enableDateRangeFilter"
          v-model="selectedDateRange"
          class="v-select m-r-10"
          :options="dateRangeOptions"
          v-bind="selectOptions"
        />
        <div v-if="enableSearch" class="m-r-15">
          <input
            v-model="filters.name.value"
            class="form-control"
            size="28"
            type="text"
            :placeholder="searchPlaceholder"
          />
        </div>
        <btn
          v-if="enableExport"
          class="btn btn-sm m-r-15 export-btn"
          :disabled="exportTableContent.length == 0"
          @click="exportTable()"
        >
          {{ $t('EXPORT_CSV') }}
        </btn>
        <Dropdown
          ref="dropdown"
          tag="span"
          :menu-right="true"
          :not-close-elements="lockedElements"
          class="bulk-actions-dropdown pull-right column-toggle"
          @hook:mounted="lockedElements = [$refs.dropdown.$el]"
        >
          <button>
            <span class="typcn typcn-cog" />
          </button>

          <template #dropdown>
            <li
              v-for="(setting, i) in toggleColumns"
              :key="i"
            >
              <div class="menu-item">
                <label :for="setting.headerName + _uid">
                  <input
                    :id="setting.headerName + _uid"
                    v-model.lazy="setting.show"
                    type="checkbox"
                    :name="setting.headerName"
                  >

                  <span>{{ $t(toggledColumnDefs[i].headerName) }}</span>
                </label>
              </div>
            </li>
          </template>
        </Dropdown>
        </div>
    </template>

    <div class="content-area">
      <keep-alive>
        <transition
          mode="out-in"
          class="o"
          type="transition"
          :appear-class="inClass"
          :appear-active-class="inClass"
          :leave-active-class="outClass"
          :leave-to-class="outClass"
        >
          <TransitionComp
            v-if="$slots.table"
            key="1"
            class="animated overflow-hidden relative"
          >
            <slot name="table" />
          </TransitionComp>
          <TransitionComp
            v-else
            key="5"
            class="overflow-hidden animated"
          >
            <SmartTable
              :column-defs="toggledColumnDefs"
              :row-data="rowData"
              :locale="widget.locale"
              :filters="filters"
              @click="$emit('click:cell', $event.row[$event.col.key])"
            >
              <template #cell="cellProps">
                <Popover
                  v-model="showPopover"
                  :target="$refs.currentSelectedCell"
                  trigger="manual"
                  placement="right"
                  custom-class="popover--slim popover--smart-table"
                >
                <slot
                  name="cell"
                  v-bind="cellProps"
                > 
                    <span 
                    v-if="enablePopoverOnClick && cellProps.value && cellProps.value !== '0' && cellProps.value !== '-' && cellProps.col.cellClass !== 'no-click'" 
                    @click="setPopoverRefs($event, cellProps)" 
                    class="clickable" 
                    :data-ref="createCellIdentificationData(cellProps)"
                    :ref="createCellIdentificationData(cellProps)"
                    >
                      {{ String(cellProps.value) || '-' }}
                    </span>
                    <span v-else
                    :data-ref="createCellIdentificationData(cellProps)"
                    :ref="createCellIdentificationData(cellProps)"
                    >
                      {{ String(cellProps.value) || '-' }}
                    </span>
                </slot>
                    <template slot="popover">
                      <div 
                      v-if="createCellIdentificationData(cellProps) == currentSelectedCell"
                      v-click-outside="{ binding: removeCurrentSelectedCell, focus: '.popover--slim' }"
                      >   
                          <Loader v-if="isLoadingPopoverData" custom-class="size-sm"/>
                          <div v-else>
                            <div class="m-b-5"><strong>{{ popoverData.title }}</strong></div>
                            <div v-if="popoverData.errorMessage" class="bs-callout bs-callout-danger p-5"> {{ popoverData.errorMessage }}</div>
                            <div v-else>
                              <CopyText v-for="item in popoverData.data" :text="item" :useTooltip="false" />
                            </div>
                          </div>
                      </div>
                    </template>
                </Popover>
              </template>
              <template
                v-for="slot in colSlots"
                v-slot:[slot]="slotProps"
              >
                <slot
                  :name="slot"
                  v-bind="slotProps"
                />
              </template>
            </SmartTable>
          </TransitionComp>
        </transition>
      </keep-alive>
    </div>
  </PortalWidget>
</template>

<script>
import sharedMessages from '@/core/locales';
import formatNumber from '@/core/filters/formatNumber';
import { csvExportService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import PortalWidget from '../PortalWidget.vue';
import { Popover } from 'uiv';
import CopyText from '../CopyText/CopyText.vue'
import {
  defaultDateRangeOptions,
  defaultSelectOptions
} from '@/modules/rtbStats/store/utils';
import SmartTable from './SmartTable.vue';

export default {
  components: {
    PortalWidget,
    SmartTable,
    Popover,
    CopyText,
    TransitionComp: {
      template: '<div><slot></slot></div>'
    }
  },
  props: {
    /** @type { Vue.PropOptions<() => Array<import('@ag-grid-community/core').ColDef>>} */
    columnDefs: {
      type: Array,
      required: true
    },

    /** @type {Vue.PropOptions<Pick<() => import('@/mock/data/rtb.json'), 'values'>[]>} */
    rowData: {
      type: Array,
      required: true
    },

    /** @type {Vue.PropOptions<data.structures.icons.FontawesomeKey>} */
    icon: {
      type: String,
      default: 'fa-key'
    },

    enableDateRangeFilter: {
      type: Boolean,
      default: true
    },

    enablePopoverOnClick: {
      type: Boolean,
      default: false
    },

    enableSearch: {
      type: Boolean,
      default: false
    },

    searchPlaceholder: {
      type: String,
      default: '',
    },

    isLoadingPopoverData: {
      type: Boolean,
      default: false
    },
    isLoadingTableData: {
      type: Boolean,
      default: false
    },

    popoverData: {
      type: Object,
      default() {
        return {};
      } 
    },

    searchKeys: {
      type: Array,
      default() {
        return [];
      }
    },

    widget: {
      type: Object,
      default: () => ({
        title: 'Title',
        iconClass: 'fa fa-plus'
      })
    },

    dateRange: {
      type: [Object, String],
      default: () => 'Today'
    },
    enableExport: {
      type: Boolean,
      default: false
    },
    exportTitle: {
      type: String,
      default: 'Export'
    }
  },
  data: () => ({
    selectedDateRange: 'Today',

    /**
     * @type {[{headerName: string, show: boolean}]}
     */
    toggleColumns: null,

    /**
     * @type {[HTMLElement]}
     */
    lockedElements: null,

    showPopover: false,

    currentSelectedCell: null,

    filters: {
      name: { value: '', keys: [] }
    },

    inClass: 'o-in',
    outClass: 'o-out'
  }),
  computed: {
    /** @type {() => any} */
    dateRangeOptions() {
      return [...defaultDateRangeOptions()];
    },

    /** @type {() => any} */
    selectOptions() {
      return {
        ...defaultSelectOptions()
      };
    },

    /** @type {() => import('@ag-grid-community/core').ColDef[]} */
    toggledColumnDefs() {
      return Array.from(this.columnDefs, (cd, i) => ({
        ...cd,
        hide: !this.toggleColumns[i].show
      }));
    },

    /** @type {() => string[]} */
    colSlots() {
      return Object.keys(this.$scopedSlots);
    },
    
    exportTableContent() {
      return Array.from(this.rowData, (record) => {
        const newRecord = {};
        this.toggleColumns.forEach((toggleCol, i) => {
          if(toggleCol.show) {
            const col = this.columnDefs[i];
            newRecord[col.field] = this.formatValue(col, record[col.field]);
          }
        });
        return newRecord;
      });
    },
    exportTitleWithFilter() {
      
      if (!this.enableDateRangeFilter) {
        return this.exportTitle;
      }

      if(this.selectedDateRange) {
        if(this.selectedDateRange.label) {
          return this.exportTitle + ' - ' + this.selectedDateRange.label;
        } else {
          return this.exportTitle + ' - ' + this.selectedDateRange;
        }
      }
      return this.exportTitle;
    },
    exportHeaders() {
      return this.columnDefs
        .filter((col, i) => this.toggleColumns[i].show)
        .map(col => ({
          label: this.$t(col.headerName),
          value: col.field,
        }));
    },
  },
  watch: {
    selectedDateRange: {
      handler(val) {
        this.$emit('update-date-range', val.label);
      }
    },
    isLoadingTableData: {
      handler() {
        if (this.isLoadingTableData) {
          this.removeCurrentSelectedCell();
        }
      }
    },
  },
  beforeMount() {
    this.selectedDateRange = this.dateRange;
    this.toggleColumns = Array.from(this.columnDefs, cd => ({
      headerName: cd.headerName,
      show: true
    }));

    if(this.widget.locale) {
      this.$i18n.setLocaleMessage('en', this.widget.locale.messages.en);
    }
  },
  created() {
    this.filters.name.keys = this.searchKeys;
  },
  methods: {
    formatValue(col, value) {
      if(col.valueFormatter?.name == 'percentFormat') {
        return this.percentFormat(value);
      }
      else if(col.valueFormatter?.name == 'currencyFormat' || col.valueFormatter?.name == 'numberFormat') {
        return formatNumber(value ?? 0);
      }
      return value;
    },
    percentFormat(value) {
      return formatNumber(value ?? 0, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + '%';
    },
    exportTable() {      
      csvExportService.exportCSVFile(this.exportHeaders, this.exportTableContent, this.exportTitleWithFilter);
    },
    togglePopover() {
      this.showPopover = !this.showPopover;
    },
    removeCurrentSelectedCell() {
      this.currentSelectedCell = null;
      if (this.showPopover) {
        this.togglePopover();
      }
    },
    setPopoverRefs(event, cellProps) {
      this.togglePopover();
      if (this.showPopover) {
        this.currentSelectedCell = event.target.dataset.ref;
        this.$emit('onClickCell', cellProps);
      }
    },
    createCellIdentificationData(cellProps) {
      return cellProps.col.key + cellProps.index
    }
  },
  i18n: {
    locale: 'en',
    sharedMessages
  },
};
</script>

<style lang="scss" scoped>
.export-btn {
  background: var(--component-reporting-settings__call-button-bar-bg) !important;
}

.table-widget {
      background: var(--component-widget-panel__holder-bg);
    box-shadow: var(--component-widget-box-shadow);

}

.header-container {
  height: 100%;
  display: flex;
  align-items: center;

  .v-select {
    margin-left: auto;
    min-width: 120px;
  }

  button {
    background-color: transparent;
    border: 0;

    > * {
      transform: translateY(-2.5px);
    }
  }
}

.column-toggle button {
  background-color: transparent;
  border: 0;
}

.dropdown-menu {
  float: none;
  max-width: none;
  font-size: unset;
}

.column-toggle {
  li {
    font-size: 0.75rem;
    color: white;
    justify-content: stretch;

    > * {
      &.menu-item {
        display: flex;
        align-items: center;
        margin: 0 !important;

        justify-content: stretch;

        > label {
          white-space: nowrap;
          display: flex;
          align-items: center;
          margin: 0;
          column-gap: 5px;
          flex: 1;
          padding: 4px 8px;

          input {
            cursor: inherit;
            margin: 0;
            padding-left: 5px;
          }

          span {
            flex: 1;
            margin-right: 5px;
          }
        }
      }
    }

    label {
      // line-height: 26px;
      font-weight: 400;
      margin-bottom: 0;
      cursor: pointer;
    }
  }
}

.content-area {
  position: relative;
  transform: translateZ(0);
  will-change: contents;
}

.animated {
  animation-duration: 200ms;
  transition-duration: 200ms;
}

</style>
